import React, { useState } from "react";
import {
  Box,
  Button,
  createStyles,
  Grid,
  makeStyles,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import _ from "lodash";
import WithLoading from "../../../../utils/WithLoading";
import { CustomTheme } from "../../../../types/customTheme";
import { handleDownloadPdf } from "./convert/handlePdf";
import { handleDownloadExcel } from "./convert/handleExcel";
import LocationDetailsCard from "../../../../components/common/LocationDetailsCard";
import MenuButtonCommon from "../../../../components/buttons/MenuButtonCommon";
import GetAppIcon from "@material-ui/icons/GetApp";
import { Autocomplete } from "@material-ui/lab";
export interface ShiftHeaderProps {
  selectedRoleObj: any;
  days: any;
  startDate: any;
  dummyData: any;
  valueTotal: any;
  value: any;
  selectedDaysList: any;
}

const useStyles = makeStyles((theme: any) =>
  createStyles({
    tableContainer: {
      overflowX: "auto", // Enable horizontal scroll if needed
      backgroundColor: "transparent",
    },
    table: {
      borderCollapse: "separate",
      borderSpacing: "4px", // Adjust spacing here (horizontal vertical)
    },
    dateCell: {
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      overflow: "hidden",
      padding: "8px",
      borderBottom: "none",
    },
    hourCell: {
      position: "sticky",
      width: "150px",
      left: 0,
      backgroundColor: theme.palette.background.default, // Ensure background is solid for fixed column
      zIndex: 1, // Ensure it stays on top of other cells when scrolling
      fontWeight: "bold",
      borderBottom: "none",
    },
    titleColor: {
      color: theme.palette.custom.orange.contrastText,
    },
    hourHeaderCell: {
      position: "sticky",
      width: "150px",
      left: 0,
      backgroundColor: theme.palette.background.default,
      zIndex: 3, // Ensure the header stays on top
      fontWeight: "bold",
      borderBottom: "none",
    },
    autoComplete: {
      marginTop: "4px",
      "& .MuiAutocomplete-input": {
        color: theme.palette.custom.orange.contrastText,
      },
      "& .MuiInputBase-root": {
        color: "inherit",
        backgroundColor: theme.palette.background.entity_highlight_background,
        borderRadius: 10,
      },
      [`& fieldset`]: {
        borderRadius: 10,
        border: "none",
        color: theme.palette.custom.orange.contrastText,
      },
    },
  }),
);

/**
 * WeeklySalesTable Component:
 *
 * This component handles the header section of the Shift component, including
 * sorting controls, location selection, and shift generation. It displays shift
 * information in a table-like format with sortable columns and provides options
 * for selecting shifts, generating reports, and handling user interactions.
 */
const WeeklySalesTable: React.FunctionComponent<ShiftHeaderProps> = ({
  selectedRoleObj,
  startDate,
  dummyData,
  valueTotal,
  value,
  selectedDaysList,
}) => {

  const theme: CustomTheme = useTheme();

  const addDaysToDate = (dateStr: any, daysToAdd: any) => {
    const date = new Date(dateStr);
    date.setDate(date.getDate() + daysToAdd);

    // Format to YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Month is 0-indexed
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const getWeekdayAbbreviation = (dateStr: any) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { weekday: "short" }); // e.g., "Thu"
  };

  const formatDateWithWeekday = (dateStr: string): any => {
    const date = new Date(dateStr);

    const weekday = date.toLocaleDateString("en-GB", { weekday: "short" });

    return (
      <div>
        <div>{weekday}</div>
        <div>{dateStr}</div>
      </div>
    );
  };

  const getColor = (value: number, list: any) => {

    const startColor = { r: 222, g: 255, b: 235 }; // #DEFFEB
    const endColor = { r: 2, g: 105, b: 87 }; // #026957
    const index = list.indexOf(value);

    const maxIndex = list.length - 1;
    const ratio = maxIndex === 0 ? 0 : index / maxIndex;
    const r = Math.round(startColor.r + (endColor.r - startColor.r) * ratio);
    const g = Math.round(startColor.g + (endColor.g - startColor.g) * ratio);
    const b = Math.round(startColor.b + (endColor.b - startColor.b) * ratio);

    const color =
    Math.round(value) === 0
        ? theme.palette.background.default === "#FFFFFF"
          ? theme.palette.background.entity_border_highlight_background
          : theme.palette.background.entity_highlight_background
        : `rgb(${r}, ${g}, ${b})`;
    return color;
  };

  /**
   * Checks if a color is dark based on luminance.
   * @param hex - Hex color string (e.g., '#FFFFFF' or '#026957')
   * @returns true if dark, false if light
   */
  const isDarkColor = (color: string): boolean => {
    let r = 0,
      g = 0,
      b = 0;

    if (color.startsWith("rgb")) {
      const match = color.match(/rgb\(\s*(\d+),\s*(\d+),\s*(\d+)\s*\)/);
      if (match) {
        r = parseInt(match[1], 10);
        g = parseInt(match[2], 10);
        b = parseInt(match[3], 10);
      } else {
        return false;
      }
    } else if (color.startsWith("#")) {
      const hex = color.replace("#", "");
      if (hex.length === 6) {
        r = parseInt(hex.substring(0, 2), 16);
        g = parseInt(hex.substring(2, 4), 16);
        b = parseInt(hex.substring(4, 6), 16);
      } else {
        return false;
      }
    } else {
      return false;
    }

    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

    return luminance < 128;
  };

  const getWeekdayAbbreviationLong = (dateStr: any) => {
    const date = new Date(dateStr);
    return date
      .toLocaleDateString("en-US", { weekday: "long" })
      .toString()
      .toLowerCase(); // e.g., "Thu"
  };

  const handleMarginRight = (isWeekendSun: any, short: any) => {
    if (selectedDaysList.includes("sunday")) {
      return isWeekendSun ? "20px" : "0px";
    } else if (short === "Sat") {
      return selectedDaysList.includes("saturday") ? "20px" : "0px";
    } else if (short === "Fri") {
      return selectedDaysList.includes("friday") ? "20px" : "0px";
    }
  };
  const normalizedDays = selectedDaysList.map((day: any) =>
    day.toLowerCase().slice(0, 3),
  );

  const isDaySelected = (inputDay: any) => {
    const inputAbbr = inputDay.toLowerCase().slice(0, 3);
    return normalizedDays.includes(inputAbbr);
  };

  const isDaySelectedNumber = (inputDay: any) => {
    if (selectedDaysList.includes("monday") && inputDay === 0) {
      return true;
    } else if (selectedDaysList.includes("tuesday") && inputDay === 1) {
      return true;
    } else if (selectedDaysList.includes("wednesday") && inputDay === 2) {
      return true;
    } else if (selectedDaysList.includes("thursday") && inputDay === 3) {
      return true;
    } else if (selectedDaysList.includes("friday") && inputDay === 4) {
      return true;
    } else if (selectedDaysList.includes("saturday") && inputDay === 5) {
      return true;
    } else if (selectedDaysList.includes("sunday") && inputDay === 6) {
      return true;
    } else {
      return false;
    }
  };

  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const classes = useStyles();
  const handleMarginLeft = (i: any) => {
    if(selectedDaysList.includes("friday")) {
      return i === 4 ? "20px" : "0px";
    } else if(selectedDaysList.includes("saturday")) {
      return i === 5 ? "20px" : "0px";
    } else if(selectedDaysList.includes("sunday")) {
      return i === 6 ? "20px" : "0px";
    }
  }
  return (
    <Table className={classes.table} size="small" stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell className={classes.hourHeaderCell}>Hour</TableCell>
          {days.map((day: any, i: any) => {
            const isNotHide = isDaySelected(day);
            const marginLeft = handleMarginLeft(i)
            if (isNotHide) {
              return (
                <TableCell
                  key={i}
                  align="center"
                  className={classes.dateCell}
                  style={{
                    fontWeight: "bold",
                    borderBottom: "none",
                    marginLeft: "20px",
                  }}
                >
                  <div style={{ marginLeft:marginLeft }}>
                    {day}
                  </div>
                </TableCell>
              );
            }
          })}
        </TableRow>
      </TableHead>
      <TableBody>
        {dummyData?.map((row: any, i: any) => (
          <TableRow key={i}>
            <TableCell
              className={classes.hourCell}
              style={{ borderBottom: "none" }}
            >
              {row.hour}
            </TableCell>
            {row?.data?.map((cell: any, j: any) => {
              // Calculate the background color based on the count
              const bgColor = getColor(
                selectedRoleObj.id === "total"
                  ? parseFloat(parseFloat(cell.total).toFixed(2))
                  : cell.count,
                selectedRoleObj.id === "total" ? valueTotal : value,
              );
              const isNotHide = isDaySelectedNumber(j);
              const marginLeft = handleMarginLeft(j)
              if (isNotHide) {
                return (
                  <TableCell
                    key={j}
                    align="center"
                    style={{
                      borderBottom: "none",
                      width: `${100 / days.length}`,
                      padding: "0px",
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: bgColor,
                        borderRadius: "10px",
                        height: selectedRoleObj.id === "all" ? "42px" : "32px",
                        marginLeft: marginLeft,
                      }}
                    >
                      {selectedRoleObj.id === "all" ? (
                        <div>
                               <div>
                          <Typography
                           variant="caption"
                            style={{
                              color: isDarkColor(bgColor) ? "white" : "black",
                            }}
                          >
                            {Math.round(cell.count)}
                          </Typography>
                          </div>
                               <div>
                          <Typography
                            variant="caption"
                            style={{
                              color: isDarkColor(bgColor) ? "white" : "black",
                              fontWeight: "bold",

                            }}
                          >
                            {cell.total === 0
                              ? "-"
                              : parseFloat(cell.total).toFixed(2)}
                          </Typography>
                          </div>
                        </div>
                      ) : (
                        <Typography
                          style={{
                            color: isDarkColor(bgColor) ? "white" : "black",
                            paddingTop: "4px",
                          }}
                        >
                          {selectedRoleObj.id === "count"
                            ? Math.round(cell.count)
                            : cell.total === 0
                            ? "-"
                            : parseFloat(cell.total).toFixed(2)}
                        </Typography>
                      )}
                    </div>
                  </TableCell>
                );
              }
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default WeeklySalesTable;
